import { BridgeComponent, BridgeElement } from "@hotwired/hotwire-native-bridge";

/**
 * Hands the top bar to the shell, which draws search on the left and inbox and the user menu
 * on the right of the screen's own bar, and the menu by the tabs. A tap replies naming the
 * control and the matching web one is clicked, so the sidebar, search, inbox and sign-out run
 * as they do on the web. The web header stays in the page, out of sight (overrides.css).
 * Mounted on the body, which outlives its controls: the bar is described again whenever one
 * comes or goes or the page morphs.
 */
export default class extends BridgeComponent {
  static component = "nav";
  static targets = ["menu", "sidebar", "search", "inbox", "user", "userItem"];

  declare readonly hasMenuTarget: boolean;
  declare readonly menuTarget: HTMLElement;
  declare readonly hasSidebarTarget: boolean;
  declare readonly sidebarTarget: HTMLElement;
  declare readonly hasSearchTarget: boolean;
  declare readonly searchTarget: HTMLElement;
  declare readonly hasInboxTarget: boolean;
  declare readonly inboxTarget: HTMLElement;
  declare readonly userTarget: HTMLElement;
  declare readonly userItemTargets: HTMLElement[];

  private active = false;
  private refreshQueued = false;

  connect() {
    super.connect();
    this.active = true;
    this.refresh();
  }

  disconnect() {
    this.active = false;
    super.disconnect();
  }

  inboxTargetConnected() {
    this.refresh();
  }

  inboxTargetDisconnected() {
    this.refresh();
  }

  userItemTargetConnected() {
    this.refresh();
  }

  userItemTargetDisconnected() {
    this.refresh();
  }

  /** One description per burst: targets connect one at a time, and a morph touches many. */
  refresh() {
    if (this.refreshQueued) return;
    this.refreshQueued = true;
    queueMicrotask(() => {
      this.refreshQueued = false;
      if (this.active && this.element.isConnected) this.notifyBridgeOfConnect();
    });
  }

  // The side menu's overlay, or layout v2's phone menu sheet.
  menuChanged(event: Event) {
    this.send("menu", { open: ["side-menu:menuOpened", "mobile-menu:opened"].includes(event.type) });
  }

  private notifyBridgeOfConnect() {
    const user = new BridgeElement(this.userTarget);
    const items = this.userItemTargets.map((element, index) => {
      const item = new BridgeElement(element);
      return {
        index,
        title: item.title,
        group: item.bridgeAttribute("group"),
        destructive: item.bridgeAttribute("destructive") === "true",
      };
    });

    this.send(
      "connect",
      {
        search: this.hasSearchTarget,
        unread: this.hasInboxTarget && new BridgeElement(this.inboxTarget).bridgeAttribute("unread") === "true",
        user: { id: user.bridgeAttribute("id"), title: user.bridgeAttribute("title"), subtitle: user.bridgeAttribute("subtitle"), items },
        menuOpen: this.menuOpen(),
      },
      (message: any) => this.tapped(message.data),
    );
  }

  /** The sidebar overlay, or layout v2's phone sheet, which its dock button tracks. */
  private menuOpen(): boolean {
    if (this.hasSidebarTarget && this.sidebarTarget.getAttribute("aria-hidden") === "false") return true;
    return this.hasMenuTarget && this.menuTarget.getAttribute("aria-expanded") === "true";
  }

  private tapped({ action, index }: { action: string; index?: number }) {
    switch (action) {
      case "menu":
        return new BridgeElement(this.menuTarget).click();
      case "search":
        return new BridgeElement(this.searchTarget).click();
      case "inbox":
        return new BridgeElement(this.inboxTarget).click();
      case "user":
        return new BridgeElement(this.userItemTargets[index!]).click();
    }
  }
}
